/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import {useMemo} from 'react';
import {useInfiniteQuery} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {searchiOnVision} from '@ion/core/src/services/Connect';
import {GenericObject} from '@ion/core/src/types/entities';
import {AssetSort, IONVisionSearchParams} from '@ion/core/src/types/services';
import {
  createKey,
  QueryKey
} from '../config/QueryKeys';
import {
  createQueryResult,
  DurationResult,
  getStaleTime
} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export interface UseVisionInfiniteQueryProps {
  deviceId: IONVisionSearchParams;
  staleTime?: number | DurationResult;
  sortOn?: AssetSort[];
  maxResults?: number;
  enabled?: boolean;
}

export interface UseVisionInfiniteQueryResult {
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
  hasNextPage: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  isLoading: boolean;
  data: GenericObject;
}

/**
 * @useVisionInfiniteQuery
 *
 * The useVisionInfiniteQuery hook a paginated query to retrieve Vision events.
 *
 * ```typescript
 *
 * import {useVisionInfiniteQuery} from '@ion/query/src/hooks/';
 *
 * ...
 *
 * const {
 * isFetchingNextPage,
 * fetchNextPage,
 * fetchNextPage,
 * hasNextPage,
 * data,
 * } = useVisionInfiniteQuery({
 *  deviceId : '1234567890',
    sortOn   : [`-${AssetSearchableProperty.avlEventTime}`],
 *
 * });
 *
 * ```
 *
 * @param deviceId - The device ID to search for
 * @param staleTime - The time in milliseconds to keep the query data in cache
 * @param sortOn - The sort order for the results
 * @param maxResults - The number of results to return per page
 * @param enabled - When true, the query will be executed immediately.
 *
 * @returns useVisionInfiniteQueryResult containing the query results and the a function to fetch the next page.
 *
 */
export const useVisionInfiniteQuery = ({
  sortOn,
  deviceId,
  staleTime = getStaleTime(5, false, false),
  maxResults = 6,
  enabled = true
}: UseVisionInfiniteQueryProps) => {
  const searchTerms = {
    avlEventTime : undefined,
    ctcDeviceId  : deviceId
  } as IONVisionSearchParams;

  const key = useMemo(() => (createKey(
    QueryKey.VISION_INFINITE_EVENTS,
    deviceId
  )), [deviceId]);

  const fetchVisionEvents = async({pageParam = 1}) => {
    try {
      return await searchiOnVision({
        searchTerms,
        pageSize : maxResults,
        pageId   : pageParam,
        sortOn
      });
    } catch (err) {
      error(err);
      return [];
    }
  };

  const query = useInfiniteQuery(
    key,
    fetchVisionEvents,
    {
      getNextPageParam : (lastPage, pages) => {
        if (lastPage?.length < maxResults) {
          return undefined;
        }
        return pages.length + 1;
      },
      staleTime : staleTime as number,
      enabled
    }
  );

  return createQueryResult(query, {
    isFetchingNextPage : query.isFetchingNextPage,
    fetchNextPage      : query.fetchNextPage,
    hasNextPage        : query.hasNextPage
  } as Partial<UseDefaultQueryResult> & UseVisionInfiniteQueryResult);
};

export default useVisionInfiniteQuery;
